import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { trackVisit } from '../utils/api';
import api from '../utils/api';
import UAEClock from '../components/UAEClock';
import { DirhamIconColored } from '../components/DirhamIcon';
import PopupNotification from '../components/PopupNotification';
import { InstallPrompt } from '../components/AppUpdate';
import ServicesCatalogSheet from '../components/ServicesCatalogSheet';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock, 
  Scissors,
  Star,
  Crown,
  Users,
  Award,
  ChevronLeft,
  ChevronRight,
  Download,
  Smartphone,
  Share,
  Quote,
  Send,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Tag,
  Percent,
  CreditCard,
  X,
  RefreshCw,
  FileText,
  Printer
} from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [salonRating, setSalonRating] = useState({ average: 0, total_ratings: 0 });
  const [barbersRatings, setBarbersRatings] = useState([]);
  
  // Services & Offers state
  const [serviceCategories, setServiceCategories] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [activeOffers, setActiveOffers] = useState([]);
  const [displaySettings, setDisplaySettings] = useState({ show_prices: true });
  const [selectedCategory, setSelectedCategory] = useState(null); // القسم المحدد
  
  // Social Media state
  const [socialMediaLinks, setSocialMediaLinks] = useState({});

  // Public services catalog PDF download
  const catalogPrintRef = useRef(null);
  const handleDownloadCatalog = useElementToPdf({
    contentRef: catalogPrintRef,
    documentTitle: `KE_Gents_Saloon_PriceList_${new Date().toISOString().slice(0, 10)}`,
    pageStyle: '@page { size: A4 portrait; margin: 0; }',
  });
  
  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    name: '',
    phone: '',
    type: 'feedback', // feedback, inquiry, suggestion
    message: ''
  });
  const [mathChallenge, setMathChallenge] = useState({ q1: '', a1: 0, q2: '', a2: 0 });
  const [userAnswers, setUserAnswers] = useState({ answer1: '', answer2: '' });
  const [feedbackStatus, setFeedbackStatus] = useState({ loading: false, success: false, error: '' });

  // Generate math challenge
  const generateMathChallenge = () => {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;
    const num3 = Math.floor(Math.random() * 9) + 1;
    const num4 = Math.floor(Math.random() * 9) + 1;
    
    setMathChallenge({
      q1: `${num1} + ${num2}`,
      a1: num1 + num2,
      q2: `${num3} + ${num4}`,
      a2: num3 + num4
    });
    setUserAnswers({ answer1: '', answer2: '' });
  };

  // Handle feedback submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    
    // Verify math answers
    if (parseInt(userAnswers.answer1) !== mathChallenge.a1 || 
        parseInt(userAnswers.answer2) !== mathChallenge.a2) {
      setFeedbackStatus({ loading: false, success: false, error: language === 'ar' ? 'إجابات التحقق غير صحيحة' : 'Verification answers are incorrect' });
      return;
    }
    
    if (!feedbackForm.name || !feedbackForm.phone || !feedbackForm.message) {
      setFeedbackStatus({ loading: false, success: false, error: language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields' });
      return;
    }
    
    setFeedbackStatus({ loading: true, success: false, error: '' });
    
    try {
      // Send feedback to WhatsApp
      const typeLabels = {
        feedback: language === 'ar' ? 'تعليق' : 'Feedback',
        inquiry: language === 'ar' ? 'استفسار' : 'Inquiry',
        suggestion: language === 'ar' ? 'اقتراح' : 'Suggestion'
      };
      
      const message = `📩 ${typeLabels[feedbackForm.type]} جديد من الموقع

👤 الاسم: ${feedbackForm.name}
📞 الهاتف: ${feedbackForm.phone}
📝 النوع: ${typeLabels[feedbackForm.type]}

💬 الرسالة:
${feedbackForm.message}

---
KE Gents Saloon Website`;
      
      // Send via WhatsApp
      const whatsappNumber = '971569810665';
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
      
      setFeedbackStatus({ loading: false, success: true, error: '' });
      setFeedbackForm({ name: '', phone: '', type: 'feedback', message: '' });
      generateMathChallenge();
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setFeedbackStatus({ loading: false, success: false, error: '' });
      }, 5000);
      
    } catch (error) {
      setFeedbackStatus({ loading: false, success: false, error: language === 'ar' ? 'حدث خطأ. يرجى المحاولة مرة أخرى' : 'An error occurred. Please try again' });
    }
  };

  // Track visit on page load and fetch ratings
  useEffect(() => {
    trackVisit('/');
    fetchRatings();
    fetchServicesAndOffers();
    fetchSocialMediaLinks();
    generateMathChallenge();
    
    // Check if iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);
    
    // Listen for PWA install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle hash scroll (for /services -> /#services-section)
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.replace('#', '');
      const element = document.querySelector(`[data-testid="${elementId}"]`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const fetchRatings = async () => {
    try {
      const [salonRes, barbersRes] = await Promise.all([
        api.get('/ratings/salon/average'),
        api.get('/ratings/barbers/all')
      ]);
      setSalonRating(salonRes.data);
      setBarbersRatings(barbersRes.data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const fetchServicesAndOffers = async () => {
    try {
      const [categoriesRes, servicesRes, offersRes, settingsRes] = await Promise.all([
        api.get('/services/categories'),
        api.get('/services'),
        api.get('/services/offers'),
        api.get('/services/display-settings')
      ]);
      setServiceCategories(categoriesRes.data);
      setServicesData(servicesRes.data);
      setActiveOffers(offersRes.data);
      setDisplaySettings(settingsRes.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const getIconForCategory = (iconName) => {
    const icons = {
      'scissors': Scissors,
      'sparkles': Sparkles,
      'hand': Award,
      'tag': Tag,
      'package': Award
    };
    return icons[iconName] || Scissors;
  };

  const fetchSocialMediaLinks = async () => {
    try {
      const res = await api.get('/social-media/links');
      setSocialMediaLinks(res.data);
    } catch (error) {
      console.error('Error fetching social media links:', error);
    }
  };

  const handleInstallPWA = async () => {
    // Show the install/update prompt modal
    setShowInstallPrompt(true);
  };

  // Check if PWA is installed
  useEffect(() => {
    const checkPWAInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone === true;
      setIsPWAInstalled(isStandalone);
    };
    checkPWAInstalled();
    
    // Listen for display mode changes
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkPWAInstalled);
    
    return () => {
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkPWAInstalled);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactInfo = {
    phone: '00971569810665',
    whatsapp: '00971569810665',
    mapUrl: 'https://maps.app.goo.gl/Z9cUisE7p8tQxA2N9',
    mapEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.5!2d55.4!3d25.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDE4JzAwLjAiTiA1NcKwMjQnMDAuMCJF!5e0!3m2!1sen!2sae!4v1234567890'
  };

  const services = [
    { name: language === 'ar' ? 'قص الشعر' : 'Haircut', icon: Scissors },
    { name: language === 'ar' ? 'تهذيب اللحية' : 'Beard Trim', icon: Scissors },
    { name: language === 'ar' ? 'صبغ الشعر' : 'Hair Color', icon: Star },
    { name: language === 'ar' ? 'علاج الشعر' : 'Hair Treatment', icon: Award },
  ];

  const handleWhatsApp = () => {
    const message = encodeURIComponent(language === 'ar' 
      ? 'مرحباً، أريد حجز موعد في KE Gents Saloon' 
      : 'Hello, I want to book an appointment at KE Gents Saloon');
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
  };

  const handleCall = () => {
    window.open(`tel:${contactInfo.phone}`, '_self');
  };

  const handleMap = () => {
    window.open(contactInfo.mapUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Popup Notifications */}
      <PopupNotification language={language} />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <img 
                src="/logo-gold.png" 
                alt="KE" 
                className="w-10 h-10 object-contain"
              />
              <span className="font-bold text-xl text-white">KE Gents Saloon</span>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* UAE Clock */}
              <UAEClock compact className="hidden md:flex" />
              
              {/* Current Language Flag - Large (2x size) */}
              <div 
                className="flex items-center gap-2 px-2 py-1 bg-primary/10 border border-primary/20 rounded-lg"
                title={language === 'ar' ? 'اللغة الحالية: العربية' : 'Current Language: English'}
              >
                {language === 'ar' ? (
                  <img 
                    src="https://flagcdn.com/w80/ae.png" 
                    alt="العربية" 
                    className="w-10 h-7 object-cover rounded shadow-md border border-white/10"
                  />
                ) : (
                  <img 
                    src="https://flagcdn.com/w80/us.png" 
                    alt="English" 
                    className="w-10 h-7 object-cover rounded shadow-md border border-white/10"
                  />
                )}
              </div>

              {/* Language Toggle - Switch to other language */}
              <Button
                onClick={toggleLanguage}
                variant="ghost"
                size="sm"
                className="flex items-center gap-2 border border-white/10"
                title={language === 'ar' ? 'Switch to English' : 'التبديل للعربية'}
              >
                {language === 'ar' ? (
                  <>
                    <img 
                      src="https://flagcdn.com/w40/us.png" 
                      alt="English" 
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                    <span className="hidden sm:inline">EN</span>
                  </>
                ) : (
                  <>
                    <img 
                      src="https://flagcdn.com/w40/ae.png" 
                      alt="العربية" 
                      className="w-6 h-4 object-cover rounded-sm"
                    />
                    <span className="hidden sm:inline">AR</span>
                  </>
                )}
              </Button>

              {/* Login Button */}
              <Button
                data-testid="login-btn-nav"
                onClick={() => navigate('/login')}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black">
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, #d4af37 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          {/* Logo */}
          <div className="mb-8 inline-flex items-center justify-center">
            <img 
              src="/logo-gold.png" 
              alt="KE Gents Saloon" 
              className="w-72 h-72 object-contain"
            />
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-bold text-white mb-4">
            KE Gents Saloon
          </h1>
          
          <p className="text-xl sm:text-2xl text-primary mb-4">
            {language === 'ar' ? 'صالون الرجال المتميز' : 'Premium Men\'s Salon'}
          </p>

          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {language === 'ar' 
              ? 'نقدم لك أفضل خدمات الحلاقة والعناية بالشعر واللحية مع فريق من المحترفين'
              : 'We offer you the best barbering and hair care services with a team of professionals'}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="book-online-btn"
              onClick={() => navigate('/public-booking')}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 text-lg px-8"
            >
              <Scissors className="w-5 h-5" />
              {language === 'ar' ? 'احجز موعدك الآن' : 'Book Now'}
            </Button>
            <Button
              data-testid="book-whatsapp-btn"
              onClick={handleWhatsApp}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white gap-2 text-lg px-8"
            >
              <MessageCircle className="w-5 h-5" />
              {language === 'ar' ? 'احجز عبر الواتساب' : 'Book via WhatsApp'}
            </Button>
            <Button
              data-testid="call-btn"
              onClick={handleCall}
              size="lg"
              variant="outline"
              className="border-primary text-primary hover:bg-primary/10 gap-2 text-lg px-8"
            >
              <Phone className="w-5 h-5" />
              {language === 'ar' ? 'اتصل الآن' : 'Call Now'}
            </Button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-primary/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-primary rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-zinc-900/50" data-testid="about-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {language === 'ar' ? 'من نحن' : 'About Us'}
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto rounded-full"></div>
          </div>
          
          <div className="bg-card/30 border border-white/10 rounded-2xl p-8 backdrop-blur-sm">
            <p className="text-lg text-muted-foreground leading-relaxed text-center">
              {language === 'ar' 
                ? 'KE Gents Saloon هو صالون للعناية بالرجال يقع في الهبوب 2، إمارة أم القيوين، الإمارات العربية المتحدة. نقدم خدمات حلاقة وعناية احترافية تشمل قص الشعر، تهذيب اللحية، صبغ الشعر، علاجات الكيراتين، والتصفيف.'
                : 'KE Gents Saloon is a men\'s grooming salon located in Al Haboob 2, Umm Al Quwain, United Arab Emirates. We provide professional barber and grooming services including haircuts, beard trimming, hair coloring, keratin treatments, and styling.'}
            </p>
            
            <div className="mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center justify-center gap-2 text-primary">
                <CreditCard className="w-5 h-5" />
                <span className="text-sm">
                  {language === 'ar'
                    ? 'نقبل الدفع في الصالون نقداً وبالبطاقة، وكذلك الدفع الإلكتروني الآمن عبر Stripe لعربون الحجز ورسوم الخدمات.'
                    : 'We accept payments in-store via cash and card (POS terminal), as well as secure online payments through Stripe for booking deposits and service fees.'}
                </span>
              </div>
            </div>
            
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-sm text-white">
                  {language === 'ar' ? 'الهبوب 2، إمارة أم القيوين' : 'Al Haboob 2, Umm Al Quwain'}
                </span>
              </div>
              <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                <Phone className="w-4 h-4 text-primary" />
                <span className="text-sm text-white" dir="ltr">+971 56 981 0665</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Dynamic from Database */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="services-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4">
            {language === 'ar' ? 'خدماتنا' : 'Our Services'}
          </h2>
          <p className="text-center text-muted-foreground mb-6">
            {language === 'ar' ? 'اختر القسم لعرض الخدمات' : 'Select a category to view services'}
          </p>

          {/* Public PDF catalog download — visible on the services section */}
          <div className="flex justify-center mb-10">
            <Button
              onClick={handleDownloadCatalog}
              size="lg"
              variant="outline"
              className="gap-2 border-primary/60 hover:bg-primary/10 text-primary"
              data-testid="public-download-catalog-btn"
              disabled={serviceCategories.length === 0}
            >
              <FileText className="w-5 h-5" />
              {language === 'ar'
                ? 'تنزيل قائمة الأسعار (PDF) — عربي / إنجليزي'
                : 'Download Price List (PDF) — Arabic / English'}
            </Button>
          </div>

          {/* Service Categories as Buttons */}
          {serviceCategories.length > 0 ? (
            <div className="space-y-8">
              {/* Categories Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {serviceCategories.map((category) => {
                  const categoryServices = servicesData.filter(s => s.category_id === category.id);
                  const IconComponent = getIconForCategory(category.icon);
                  const isSelected = selectedCategory?.id === category.id;
                  
                  if (categoryServices.length === 0) return null;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(isSelected ? null : category)}
                      className={`
                        bg-card border rounded-2xl p-6 text-center transition-all duration-300
                        hover:transform hover:scale-105
                        ${isSelected 
                          ? 'border-primary bg-primary/20 shadow-lg shadow-primary/20' 
                          : 'border-white/10 hover:border-primary/50'
                        }
                      `}
                      data-testid={`category-btn-${category.id}`}
                    >
                      <div className={`
                        w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors
                        ${isSelected ? 'bg-primary/30' : 'bg-primary/10'}
                      `}>
                        <IconComponent className={`w-8 h-8 ${isSelected ? 'text-primary' : 'text-primary/70'}`} />
                      </div>
                      <h3 className={`font-semibold ${isSelected ? 'text-primary' : 'text-white'}`}>
                        {language === 'ar' ? category.name_ar : category.name_en}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {categoryServices.length} {language === 'ar' ? 'خدمة' : 'services'}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Selected Category Services */}
              {selectedCategory && (
                <div className="bg-card/30 border border-primary/30 rounded-2xl p-6 animate-in slide-in-from-top-4 duration-300">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                        {(() => {
                          const IconComponent = getIconForCategory(selectedCategory.icon);
                          return <IconComponent className="w-6 h-6 text-primary" />;
                        })()}
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {language === 'ar' ? selectedCategory.name_ar : selectedCategory.name_en}
                      </h3>
                    </div>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="text-muted-foreground hover:text-white transition-colors p-2"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {servicesData
                      .filter(s => s.category_id === selectedCategory.id)
                      .map((service) => (
                        <div
                          key={service.id}
                          className="bg-card border border-white/10 rounded-xl p-4 hover:border-primary/50 transition-all"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-white">
                                {language === 'ar' ? service.name_ar : service.name_en}
                              </h4>
                              {service.description_ar && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {language === 'ar' ? service.description_ar : service.description_en}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                                <Clock className="w-4 h-4" />
                                <span>{service.duration_minutes} {language === 'ar' ? 'دقيقة' : 'min'}</span>
                              </div>
                            </div>
                            {/* Show price based on: global settings AND category setting AND individual service setting */}
                            {displaySettings.show_prices && selectedCategory.show_price !== false && service.show_price !== false && (
                              <div className="text-lg font-bold text-primary">
                                {service.price} <DirhamIconColored className="w-4 h-4 inline" color="primary" />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Fallback to static services if no data from API */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {services.map((service) => (
                <div
                  key={service.id || service.name}
                  className="bg-card border border-white/10 rounded-2xl p-6 text-center hover:border-primary/50 transition-all hover:transform hover:scale-105"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <service.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-white">{service.name}</h3>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Active Offers Section */}
      {activeOffers.length > 0 && (
        <section className="py-20 px-4 bg-gradient-to-b from-primary/10 to-transparent" data-testid="offers-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-2xl mb-6">
                <Percent className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                {language === 'ar' ? 'عروضنا الخاصة' : 'Special Offers'}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                {language === 'ar' 
                  ? 'استفد من عروضنا المميزة لفترة محدودة'
                  : 'Take advantage of our limited-time special offers'}
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {activeOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-card border border-primary/30 rounded-2xl p-6 relative overflow-hidden group hover:border-primary transition-all"
                  data-testid={`offer-card-${offer.id}`}
                >
                  {/* Discount Badge */}
                  <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-bold">
                    -{Math.round((1 - offer.discount_price / offer.original_price) * 100)}%
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-white mb-2">
                      {language === 'ar' ? offer.title_ar : offer.title_en}
                    </h3>
                    {offer.description_ar && (
                      <p className="text-sm text-muted-foreground">
                        {language === 'ar' ? offer.description_ar : offer.description_en}
                      </p>
                    )}
                  </div>
                  
                  {/* Show prices based on offer's show_price setting */}
                  {offer.show_price !== false ? (
                    <div className="flex items-center gap-3 mb-4">
                                                                                <span className="text-muted-foreground line-through text-lg">{offer.original_price} <DirhamIconColored className="w-4 h-4 inline" color="muted" /></span>
                      <span className="text-3xl font-bold text-primary">{offer.discount_price} <DirhamIconColored className="w-5 h-5 inline" color="primary" /></span>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <span className="text-sm text-muted-foreground">
                        {language === 'ar' ? 'اتصل لمعرفة السعر' : 'Call for price'}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <Clock className="w-4 h-4" />
                    <span>
                      {language === 'ar' ? 'ينتهي: ' : 'Ends: '}{offer.end_date}
                    </span>
                  </div>
                  
                  <Button
                    onClick={handleWhatsApp}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {language === 'ar' ? 'احجز الآن' : 'Book Now'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section — جاهز لتجربة مميزة */}
      <section className="py-20 px-4 bg-gradient-to-r from-primary/20 to-primary/5" data-testid="cta-experience-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {language === 'ar' ? 'جاهز لتجربة مميزة؟' : 'Ready for a Great Experience?'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {language === 'ar' 
              ? 'احجز موعدك الآن واستمتع بأفضل خدمات الحلاقة'
              : 'Book your appointment now and enjoy the best barbering services'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsApp}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white gap-2 text-lg px-8"
            >
              <MessageCircle className="w-5 h-5" />
              {language === 'ar' ? 'احجز الآن' : 'Book Now'}
            </Button>
            <Button
              onClick={() => navigate('/rate-us')}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 text-lg px-8"
              data-testid="rate-us-btn"
            >
              <Star className="w-5 h-5" />
              {language === 'ar' ? 'قيّمنا' : 'Rate Us'}
            </Button>
            <Button
              onClick={() => navigate('/loyalty')}
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white gap-2 text-lg px-8"
              data-testid="loyalty-card-btn"
            >
              <Crown className="w-5 h-5" />
              {language === 'ar' ? 'بطاقة الولاء' : 'Loyalty Card'}
            </Button>
            <Button
              onClick={() => navigate('/login')}
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 gap-2 text-lg px-8"
            >
              <Users className="w-5 h-5" />
              {language === 'ar' ? 'دخول الموظفين' : 'Staff Login'}
            </Button>
          </div>
        </div>
      </section>

      {/* Download App Section */}
      <section className="py-16 px-4 bg-zinc-900/50" data-testid="download-app-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/20 rounded-2xl mb-6">
            <Smartphone className="w-10 h-10 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {language === 'ar' ? 'حمّل التطبيق الآن' : 'Download the App'}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            {language === 'ar' 
              ? (isPWAInstalled ? 'تحقق من وجود تحديثات جديدة للتطبيق' : 'احصل على تجربة أفضل مع تطبيقنا. احجز مواعيدك بسهولة من هاتفك')
              : (isPWAInstalled ? 'Check for app updates' : 'Get a better experience with our app. Book your appointments easily from your phone')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* Android / Chrome Install */}
            <Button
              data-testid="install-android-btn"
              onClick={handleInstallPWA}
              size="lg"
              className={`${isPWAInstalled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white gap-3 text-lg px-8`}
            >
              {isPWAInstalled ? <RefreshCw className="w-6 h-6" /> : <Download className="w-6 h-6" />}
              <div className="text-start">
                <div className="text-xs opacity-75">
                  {language === 'ar' ? (isPWAInstalled ? 'تحديث على' : 'تحميل على') : (isPWAInstalled ? 'Update on' : 'Install on')}
                </div>
                <div className="font-bold">Android</div>
              </div>
            </Button>

            {/* iOS Install */}
            <Button
              data-testid="install-ios-btn"
              onClick={handleInstallPWA}
              size="lg"
              className={`${isPWAInstalled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-zinc-800 hover:bg-zinc-700'} text-white gap-3 text-lg px-8 border border-white/10`}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
              </svg>
              <div className="text-start">
                <div className="text-xs opacity-75">
                  {language === 'ar' ? (isPWAInstalled ? 'تحديث على' : 'تحميل على') : (isPWAInstalled ? 'Update on' : 'Install on')}
                </div>
                <div className="font-bold">iPhone</div>
              </div>
            </Button>
          </div>

          {/* Installation Instructions Note */}
          <p className="text-sm text-muted-foreground mt-6">
            {language === 'ar' 
              ? 'التطبيق يعمل بدون إنترنت ويرسل إشعارات فورية'
              : 'The app works offline and sends instant notifications'}
          </p>
        </div>
      </section>

      {/* Contact & Map Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-white mb-4">
            {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
          </h2>
          <p className="text-center text-muted-foreground mb-12">
            {language === 'ar' ? 'نحن هنا لخدمتك' : 'We are here to serve you'}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              {/* Phone */}
              <div 
                onClick={handleCall}
                className="bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-primary/50 transition-all"
              >
                <div className="w-14 h-14 bg-blue-500/10 rounded-xl flex items-center justify-center">
                  <Phone className="w-7 h-7 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'للاتصال بالصالون' : 'Call the Salon'}
                  </p>
                  <p className="text-xl font-bold text-white" dir="ltr">+971 56 981 0665</p>
                </div>
              </div>

              {/* WhatsApp */}
              <div 
                onClick={handleWhatsApp}
                className="bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-green-500/50 transition-all"
              >
                <div className="w-14 h-14 bg-green-500/10 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-7 h-7 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'للتواصل مع الإدارة' : 'Contact Management'}
                  </p>
                  <p className="text-xl font-bold text-white" dir="ltr">+971 56 981 0665</p>
                </div>
              </div>

              {/* Location */}
              <div 
                onClick={handleMap}
                className="bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-red-500/50 transition-all"
              >
                <div className="w-14 h-14 bg-red-500/10 rounded-xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-red-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'موقعنا' : 'Our Location'}
                  </p>
                  <p className="text-lg font-bold text-white">
                    {language === 'ar' ? 'اضغط لفتح الخريطة' : 'Click to open map'}
                  </p>
                </div>
              </div>

              {/* Working Hours */}
              <div className="bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'ar' ? 'ساعات العمل' : 'Working Hours'}
                  </p>
                  <p className="text-lg font-bold text-white">
                    {language === 'ar' ? 'يومياً: 9 ص - 12 منتصف الليل' : 'Daily: 9 AM - 12 AM'}
                  </p>
                </div>
              </div>

              {/* Social Media - Instagram & TikTok */}
              <div className="flex gap-4">
                {/* Instagram */}
                <a
                  href="https://www.instagram.com/kegentssaloon/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-pink-500/50 transition-all group"
                  data-testid="contact-instagram"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'ar' ? 'تابعنا على' : 'Follow us on'}
                    </p>
                    <p className="text-lg font-bold text-white">Instagram</p>
                  </div>
                </a>

                {/* TikTok */}
                <a
                  href="https://www.tiktok.com/@ke.gents.saloon.u"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 bg-card border border-white/10 rounded-2xl p-6 flex items-center gap-4 cursor-pointer hover:border-white/50 transition-all group"
                  data-testid="contact-tiktok"
                >
                  <div className="w-14 h-14 bg-black border border-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {language === 'ar' ? 'تابعنا على' : 'Follow us on'}
                    </p>
                    <p className="text-lg font-bold text-white">TikTok</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Map */}
            <div className="bg-card border border-white/10 rounded-2xl overflow-hidden h-[400px] md:h-auto">
              <iframe
                src="https://maps.google.com/maps?q=K+E+Gents+Saloon+%D8%B5%D8%A7%D9%84%D9%88%D9%86+%D9%83%D9%8A+%D8%A7%D9%8A+%D9%84%D9%84%D8%B1%D8%AC%D8%A7%D9%84&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="KE Gents Saloon Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Ratings Section */}
      <section className="py-20 px-4 bg-zinc-900/50" data-testid="ratings-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-2xl mb-6">
              <Star className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {language === 'ar' ? 'آراء عملائنا' : 'Customer Reviews'}
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {language === 'ar' 
                ? 'نفتخر بثقة عملائنا وتقييماتهم الإيجابية'
                : 'We are proud of our customers trust and positive reviews'}
            </p>
          </div>

          {/* Overall Rating */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-8 mb-12">
            {/* Salon Rating */}
            <div className="bg-card border border-white/10 rounded-2xl p-8 text-center w-full md:w-auto md:min-w-[280px]">
              <div className="text-6xl font-bold text-primary mb-2">{salonRating.average}</div>
              <div className="flex justify-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`w-6 h-6 ${
                      star <= Math.round(salonRating.average)
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-600'
                    }`}
                  />
                ))}
              </div>
              <p className="text-muted-foreground font-medium">
                {language === 'ar' ? 'إدارة الصالون' : 'Salon Management'}
              </p>
              <p className="text-xs text-primary/80 mt-0.5">
                {language === 'ar' ? '(الصالون بشكل عام)' : '(Overall salon)'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {salonRating.total_ratings} {language === 'ar' ? 'تقييم' : 'reviews'}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
              <div className="bg-card border border-white/10 rounded-xl p-6 text-center">
                <Award className="w-8 h-8 text-primary mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{barbersRatings.length}</div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'حلاق محترف' : 'Professional Barbers'}
                </p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-6 text-center">
                <Users className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">+500</div>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' ? 'عميل سعيد' : 'Happy Customers'}
                </p>
              </div>
            </div>
          </div>

          {/* Barbers Ratings */}
          {barbersRatings.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {barbersRatings.map((barber, index) => (
                <div 
                  key={barber.barber_id}
                  className="bg-card border border-white/10 rounded-2xl p-6 hover:border-primary/30 transition-all"
                  data-testid={`barber-card-${barber.barber_id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center">
                      {index === 0 && barber.average > 0 ? (
                        <Award className="w-7 h-7 text-yellow-400" />
                      ) : (
                        <Scissors className="w-7 h-7 text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-lg">{barber.barber_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-4 h-4 ${
                                star <= Math.round(barber.average)
                                  ? 'fill-yellow-400 text-yellow-400'
                                  : 'text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="text-primary font-bold">{barber.average}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {barber.total_ratings} {language === 'ar' ? 'تقييم' : 'reviews'}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Ratings Yet */}
          {salonRating.total_ratings === 0 && barbersRatings.every(b => b.total_ratings === 0) && (
            <div className="text-center py-12">
              <Quote className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">
                {language === 'ar' 
                  ? 'كن أول من يقيّم خدماتنا!'
                  : 'Be the first to rate our services!'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Install/Update Prompt Modal */}
      <InstallPrompt show={showInstallPrompt} onClose={() => setShowInstallPrompt(false)} />

      {/* iOS Installation Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/20 rounded-2xl mb-4">
                <Share className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">
                {language === 'ar' ? 'تثبيت على iPhone' : 'Install on iPhone'}
              </h3>
            </div>
            
            <div className="space-y-4 text-start">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">1</span>
                </div>
                <p className="text-muted-foreground">
                  {language === 'ar' 
                    ? 'اضغط على زر المشاركة في متصفح Safari'
                    : 'Tap the Share button in Safari browser'}
                  <Share className="w-4 h-4 inline mx-1" />
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">2</span>
                </div>
                <p className="text-muted-foreground">
                  {language === 'ar' 
                    ? 'اختر "إضافة إلى الشاشة الرئيسية"'
                    : 'Select "Add to Home Screen"'}
                </p>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-primary font-bold">3</span>
                </div>
                <p className="text-muted-foreground">
                  {language === 'ar' 
                    ? 'اضغط "إضافة" لتثبيت التطبيق'
                    : 'Tap "Add" to install the app'}
                </p>
              </div>
            </div>
            
            <Button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 bg-primary text-primary-foreground"
            >
              {language === 'ar' ? 'فهمت' : 'Got it'}
            </Button>
          </div>
        </div>
      )}

      {/* Feedback Section */}
      <section className="py-20 px-4" data-testid="feedback-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {language === 'ar' ? 'شاركنا رأيك' : 'Share Your Feedback'}
            </h2>
            <p className="text-muted-foreground text-lg">
              {language === 'ar' 
                ? 'نحن نقدر آراءكم واستفساراتكم ومقترحاتكم لتحسين خدماتنا'
                : 'We value your feedback, inquiries, and suggestions to improve our services'}
            </p>
          </div>

          <div className="bg-card border border-white/10 rounded-2xl p-6 sm:p-8">
            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
              {/* Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {language === 'ar' ? 'الاسم' : 'Name'} *
                  </label>
                  <input
                    type="text"
                    value={feedbackForm.name}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                    className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                    placeholder={language === 'ar' ? 'اسمك الكريم' : 'Your name'}
                    data-testid="feedback-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {language === 'ar' ? 'رقم الهاتف' : 'Phone'} *
                  </label>
                  <input
                    type="tel"
                    value={feedbackForm.phone}
                    onChange={(e) => setFeedbackForm({ ...feedbackForm, phone: e.target.value })}
                    className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                    placeholder={language === 'ar' ? '05xxxxxxxx' : '05xxxxxxxx'}
                    dir="ltr"
                    data-testid="feedback-phone"
                  />
                </div>
              </div>

              {/* Type Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'نوع الرسالة' : 'Message Type'} *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'feedback', labelAr: 'تعليق', labelEn: 'Feedback', icon: '💬' },
                    { value: 'inquiry', labelAr: 'استفسار', labelEn: 'Inquiry', icon: '❓' },
                    { value: 'suggestion', labelAr: 'اقتراح', labelEn: 'Suggestion', icon: '💡' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackForm({ ...feedbackForm, type: type.value })}
                      className={`p-3 rounded-lg border transition-all ${
                        feedbackForm.type === type.value
                          ? 'bg-primary/20 border-primary text-primary'
                          : 'bg-zinc-800/50 border-white/10 text-muted-foreground hover:border-white/30'
                      }`}
                      data-testid={`feedback-type-${type.value}`}
                    >
                      <span className="text-xl mb-1 block">{type.icon}</span>
                      <span className="text-sm">{language === 'ar' ? type.labelAr : type.labelEn}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'رسالتك' : 'Your Message'} *
                </label>
                <textarea
                  value={feedbackForm.message}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                  rows={4}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none"
                  placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                  data-testid="feedback-message"
                />
              </div>

              {/* Math Verification */}
              <div className="bg-zinc-900/50 rounded-lg p-4 border border-white/5">
                <label className="block text-sm font-medium text-foreground mb-3">
                  {language === 'ar' ? '🔒 التحقق الأمني - أجب على السؤالين' : '🔒 Security Verification - Answer both questions'}
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">
                      {mathChallenge.q1} = ?
                    </label>
                    <input
                      type="number"
                      value={userAnswers.answer1}
                      onChange={(e) => setUserAnswers({ ...userAnswers, answer1: e.target.value })}
                      className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
                      placeholder={language === 'ar' ? 'الإجابة' : 'Answer'}
                      data-testid="feedback-math-1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">
                      {mathChallenge.q2} = ?
                    </label>
                    <input
                      type="number"
                      value={userAnswers.answer2}
                      onChange={(e) => setUserAnswers({ ...userAnswers, answer2: e.target.value })}
                      className="w-full bg-zinc-800/50 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary"
                      placeholder={language === 'ar' ? 'الإجابة' : 'Answer'}
                      data-testid="feedback-math-2"
                    />
                  </div>
                </div>
              </div>

              {/* Status Messages */}
              {feedbackStatus.error && (
                <div className="flex items-center gap-2 text-red-500 bg-red-500/10 p-3 rounded-lg">
                  <AlertCircle className="w-5 h-5" />
                  <span>{feedbackStatus.error}</span>
                </div>
              )}
              
              {feedbackStatus.success && (
                <div className="flex items-center gap-2 text-green-500 bg-green-500/10 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <span>{language === 'ar' ? 'تم فتح واتساب لإرسال رسالتك!' : 'WhatsApp opened to send your message!'}</span>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={feedbackStatus.loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 text-lg"
                data-testid="feedback-submit"
              >
                {feedbackStatus.loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    {language === 'ar' ? 'إرسال عبر الواتساب' : 'Send via WhatsApp'}
                  </span>
                )}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      {(socialMediaLinks.instagram || socialMediaLinks.tiktok || socialMediaLinks.snapchat || 
        socialMediaLinks.facebook || socialMediaLinks.twitter || socialMediaLinks.youtube) && (
        <section className="py-16 px-4 bg-zinc-900/50" data-testid="social-media-section">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'ar' ? 'تابعنا على' : 'Follow Us'}
            </h2>
            <p className="text-muted-foreground mb-8">
              {language === 'ar' ? 'ابق على اطلاع بآخر أخبارنا وعروضنا' : 'Stay updated with our latest news and offers'}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {socialMediaLinks.instagram && (
                <a
                  href={socialMediaLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl hover:opacity-90 transition-all"
                  data-testid="social-instagram"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </a>
              )}
              
              {socialMediaLinks.tiktok && (
                <a
                  href={socialMediaLinks.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all"
                  data-testid="social-tiktok"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  TikTok
                </a>
              )}
              
              {socialMediaLinks.snapchat && (
                <a
                  href={socialMediaLinks.snapchat}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-yellow-400 text-black px-6 py-3 rounded-xl hover:bg-yellow-300 transition-all"
                  data-testid="social-snapchat"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-.809-.329-1.213-.72-1.213-1.168 0-.359.285-.689.734-.838.137-.06.314-.091.494-.091.12 0 .284.015.434.06.389.12.748.3 1.048.359.149.03.299.045.404.045.18 0 .315-.044.399-.119-.016-.135-.03-.315-.06-.51l-.004-.06c-.104-1.628-.229-3.654.3-4.847C3.454 1.068 6.811.793 7.8.793h4.406z"/>
                  </svg>
                  Snapchat
                </a>
              )}
              
              {socialMediaLinks.facebook && (
                <a
                  href={socialMediaLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all"
                  data-testid="social-facebook"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </a>
              )}
              
              {socialMediaLinks.twitter && (
                <a
                  href={socialMediaLinks.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-black text-white px-6 py-3 rounded-xl border border-white/20 hover:bg-white/10 transition-all"
                  data-testid="social-twitter"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X
                </a>
              )}
              
              {socialMediaLinks.youtube && (
                <a
                  href={socialMediaLinks.youtube}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-all"
                  data-testid="social-youtube"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  YouTube
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img 
              src="/logo-gold.png" 
              alt="KE Gents Saloon" 
              className="w-10 h-10 object-contain"
            />
            <span className="font-bold text-white">KE Gents Saloon</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2026 KE Gents Saloon. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}
          </p>
        </div>
      </footer>

      {/* Off-screen printable services & price catalog (with contact QR codes) */}
      <div
        style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      >
        <ServicesCatalogSheet
          ref={catalogPrintRef}
          categories={serviceCategories}
          services={servicesData}
          offers={activeOffers}
          showContactQRs={true}
        />
      </div>
    </div>
  );
};

export default HomePage;
